"use client";

import { Bar, CartesianGrid, BarChart as RechartsBarChart, XAxis } from "recharts";

import { BarSalesResponseDto } from "@/lib/interfaces/dashboard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "./chart";

const COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3, 215 50% 23%))",
  "hsl(var(--chart-4, 142 76% 36%))",
  "hsl(var(--chart-5, 346 77% 49%))",
];

const BarChart = ({ title, description, data }: { title: string; description: string; data: BarSalesResponseDto }) => {
  // Transform data for the chart
  const transformedData = data.sets.map((set) => ({
    name: set.label,
    ...set.values.reduce(
      (acc, val) => ({
        ...acc,
        [val.label]: val.value,
      }),
      {}
    ),
  }));

  // Get all unique value labels to create bars
  const valueLabels = Array.from(new Set(data.sets.flatMap((set) => set.values.map((v) => v.label))));

  // Create chart config
  const chartConfig = valueLabels.reduce(
    (config, label, index) => ({
      ...config,
      [label]: {
        label,
        color: COLORS[index % COLORS.length],
      },
    }),
    {}
  ) satisfies ChartConfig;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig}>
          <RechartsBarChart data={transformedData}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="name" tickLine={false} tickMargin={10} axisLine={false} />
            <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dashed" />} />
            {valueLabels.map((label, index) => (
              <Bar key={label} dataKey={label} fill={COLORS[index % COLORS.length]} radius={[4, 4, 0, 0]} />
            ))}
          </RechartsBarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};

export default BarChart;
